import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { Button } from '../../ui/Button/Button';
import { type ScorerWeights, type BatchScalingStatValue, DEFAULT_SCORER_WEIGHTS } from '../../services/devRankingService';

const STAT_OPTIONS: BatchScalingStatValue[] = ['Max', 'Mean', 'Median', 'P90', 'P95'];

const NUMBER_FIELDS = ['weightDelay', 'weightAmount', 'weightComplexity', 'weightCredit', 'weightHistory'] as const;
type NumberField = typeof NUMBER_FIELDS[number];

const WEIGHT_LABEL_KEYS: Record<NumberField, string> = {
    weightDelay:      TranslationKey.DevRanking.Breakdown.Delay,
    weightAmount:     TranslationKey.DevRanking.Breakdown.Amount,
    weightComplexity: TranslationKey.DevRanking.Breakdown.Complexity,
    weightCredit:     TranslationKey.DevRanking.Breakdown.Credit,
    weightHistory:    TranslationKey.DevRanking.Breakdown.History,
};

interface ScorerWeightsPanelProps {
    weights: ScorerWeights;
    onWeightsChange: (weights: ScorerWeights) => void;
    onReset: () => void;
    onSubmit: () => void | Promise<void>;
    isLoading: boolean;
    status: string | null;
    submitIdleLabel: string;
    submitRunningLabel: string;
    testIdPrefix?: string;
}

export const ScorerWeightsPanel: React.FC<ScorerWeightsPanelProps> = ({
    weights,
    onWeightsChange,
    onReset,
    onSubmit,
    isLoading,
    status,
    submitIdleLabel,
    submitRunningLabel,
    testIdPrefix = 'scorer-weights',
}) => {
    const { t } = useTranslation();

    const [inputValues, setInputValues] = useState<Record<NumberField, string>>(() =>
        Object.fromEntries(NUMBER_FIELDS.map(f => [f, String(weights[f])])) as Record<NumberField, string>
    );

    useEffect(() => {
        setInputValues(
            Object.fromEntries(NUMBER_FIELDS.map(f => [f, String(weights[f])])) as Record<NumberField, string>
        );
    }, [weights.weightDelay, weights.weightAmount, weights.weightComplexity, weights.weightCredit, weights.weightHistory]);

    const handleNumberChange = (field: NumberField, value: string) => {
        setInputValues(prev => ({ ...prev, [field]: value }));
        const parsed = parseFloat(value);
        if (!isNaN(parsed)) {
            onWeightsChange({ ...weights, [field]: parsed });
        }
    };

    const handleStatChange = (field: keyof ScorerWeights, value: string) => {
        onWeightsChange({ ...weights, [field]: value as BatchScalingStatValue });
    };

    return (
        <div className="flex flex-col gap-l">
            <div className="grid grid-cols-2 gap-m sm:grid-cols-3 lg:grid-cols-5">
                {NUMBER_FIELDS.map(field => (
                    <label key={field} className="flex flex-col gap-xs text-s">
                        <span className="font-normal capitalize text-primary-700">{t(WEIGHT_LABEL_KEYS[field])}</span>
                        <input
                            type="number"
                            step="0.05"
                            min="0"
                            value={inputValues[field]}
                            onChange={e => handleNumberChange(field, e.target.value)}
                            data-testid={`${testIdPrefix}-${field}`}
                            className="rounded border border-primary-300 bg-primary-50 px-s py-xs text-s text-primary-1000 focus:outline-none focus:ring-1 focus:ring-accent-400"
                        />
                    </label>
                ))}
            </div>

            <div className="flex flex-wrap gap-l">
                <label className="flex flex-col gap-xs text-s">
                    <span className="font-normal text-primary-700">{t(TranslationKey.DevRanking.AmountScalingStat)}</span>
                    <select
                        value={weights.amountScalingStat}
                        onChange={e => handleStatChange('amountScalingStat', e.target.value)}
                        data-testid={`${testIdPrefix}-amountScalingStat`}
                        className="rounded border border-primary-300 bg-primary-50 px-s py-xs text-s text-primary-1000 focus:outline-none focus:ring-1 focus:ring-accent-400"
                    >
                        {STAT_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                </label>

                <label className="flex flex-col gap-xs text-s">
                    <span className="font-normal text-primary-700">{t(TranslationKey.DevRanking.DaysOverdueScalingStat)}</span>
                    <select
                        value={weights.daysOverdueScalingStat}
                        onChange={e => handleStatChange('daysOverdueScalingStat', e.target.value)}
                        data-testid={`${testIdPrefix}-daysOverdueScalingStat`}
                        className="rounded border border-primary-300 bg-primary-50 px-s py-xs text-s text-primary-1000 focus:outline-none focus:ring-1 focus:ring-accent-400"
                    >
                        {STAT_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                </label>
            </div>

            <div className="flex items-center gap-m">
                <Button
                    onClick={onReset}
                    disabled={isLoading}
                    data-testid={`${testIdPrefix}-reset`}
                    variant="secondary"
                >
                    {t(TranslationKey.DevRanking.Reset)}
                </Button>
                <Button
                    onClick={onSubmit}
                    loading={isLoading}
                    data-testid={`${testIdPrefix}-submit`}
                >
                    {isLoading ? submitRunningLabel : submitIdleLabel}
                </Button>
                {status && (
                    <span className="text-s text-primary-600" data-testid={`${testIdPrefix}-status`}>{status}</span>
                )}
            </div>
        </div>
    );
};

export { DEFAULT_SCORER_WEIGHTS };
